import React from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleNotch,
  faSearch,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import SegmentedControl from "../segmented-control";
import { PROVIDER_LANES } from "./providersData";

type IProviderType = "Sales" | "Renewals" | "all";
type IProductType = "Standard" | "Non-Standard" | "all";

interface IProviderFilterBar {
  type: IProviderType;
  setType: (value: IProviderType) => void;
  productType: IProductType;
  setProductType: (value: IProductType) => void;
  searchRef: React.MutableRefObject<HTMLInputElement>;
  search: string;
  setSearch: (value: string) => void;
  loading: boolean;
}

const TYPE_OPTIONS: { label: string; value: IProviderType }[] = [
  { label: "All", value: "all" },
  { label: "Sales", value: "Sales" },
  { label: "Renewals", value: "Renewals" },
];

const PRODUCT_OPTIONS: { label: string; value: IProductType }[] = [
  { label: "All", value: "all" },
  { label: "Standard", value: "Standard" },
  { label: "Non-Standard", value: "Non-Standard" },
];

const ProviderFilterBar: React.FC<IProviderFilterBar> = ({
  type,
  setType,
  productType,
  setProductType,
  searchRef,
  search,
  setSearch,
  loading,
}) => {
  const hasFilters =
    type !== "all" || productType !== "all" || !!search;

  // ---------------------------------------- [ CLEAR ALL FILTERS ] ---------------------------
  const HandleClearFilters = () => {
    setType("all");
    setProductType("all");
    if (searchRef.current) searchRef.current.value = "";
    setSearch("");
  };

  return (
    <div className="relative z-20 mt-5 mr-1 smallTablet:mr-0 bg-surface border border-edge rounded-2xl shadow-card px-4 py-3 animate-fade-in">
      <div className="flex items-center gap-x-5 gap-y-3 flex-wrap">
        <SegmentedControl
          label="Policy"
          options={TYPE_OPTIONS}
          value={type}
          onChange={setType}
        />

        <div className="h-6 w-px bg-edge smallTablet:hidden" />

        <SegmentedControl
          label="Department"
          options={PRODUCT_OPTIONS}
          value={productType}
          onChange={setProductType}
        />

        <div className="ml-auto flex items-center gap-2.5 smallTablet:ml-0 smallTablet:w-full">
          <div className="h-9 w-[240px] smallTablet:w-full flex items-center rounded-lg bg-input-bg border border-transparent focus-within:border-indigo-400/40 focus-within:ring-2 focus-within:ring-indigo-400/20 transition-all duration-200 px-3">
            <FontAwesomeIcon
              icon={loading ? faCircleNotch : faSearch}
              className={clsx("text-[12px] text-light-gray mr-2.5 shrink-0", loading && "fa-spin")}
            />
            <input
              ref={searchRef}
              type="text"
              autoComplete="off"
              aria-label="Search insurers"
              placeholder="Search insurers"
              className="flex-grow min-w-0 bg-inherit text-[13px] text-text-primary placeholder:text-light-gray font-medium focus:outline-none"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {hasFilters && (
            <button
              type="button"
              onClick={HandleClearFilters}
              className="h-9 px-3 flex items-center gap-1.5 rounded-lg text-[13px] font-medium text-light-gray hover:text-text-primary hover:bg-overlay-hover transition-colors whitespace-nowrap"
            >
              <FontAwesomeIcon icon={faXmark} className="text-[12px]" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* --------------------------- [ LEGEND ] ------------------- */}
      <div className="mt-3 pt-3 border-t border-edge-faint flex items-center gap-x-4 gap-y-2 flex-wrap text-[12px] font-medium text-light-gray">
        {PROVIDER_LANES.map((lane) => (
          <span key={lane.id} className="flex items-center gap-1.5">
            <span className={clsx("h-2 w-2 rounded-full", lane.dot)} />
            {lane.label}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <span className="h-4 px-1 rounded bg-gradient-to-r from-accent to-accent-bright text-[9px] font-bold leading-4 text-white">
            LN
          </span>
          <span className="h-4 px-1 rounded bg-gradient-to-r from-accent to-accent-bright text-[9px] font-bold leading-4 text-white">
            MM
          </span>
          Sub-tags
        </span>
      </div>
    </div>
  );
};

export default ProviderFilterBar;
